import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Input, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { COMPANY_TYPES, COMPANY_TYPE_LABEL_MAP } from "../../../../constants/companyTypes";
import {
    fetchIsyerimPosApplicationDefaults,
    submitIsyerimPosApplication,
} from "../../../../api/requests/company-requests";

const NAVY = "#0A316E";
const PRODUCT = "Sanal POS Entegrasyonu";
const PHONE_PATTERN = /^[0-9+() -]{10,20}$/;
const NATIONAL_ID_PATTERN = /^[1-9][0-9]{10}$/;

const STEP_ONE_FIELDS = ["firstName", "lastName", "email", "phone", "companyType", "kvkkAccepted"];

const STEPS = [
    { no: 1, label: "Yetkili Bilgileri" },
    { no: 2, label: "İşletme Bilgileri" },
];

const validationSchema = Yup.object({
    firstName: Yup.string().trim().required("Yetkili adı gerekli").max(60, "En fazla 60 karakter"),
    lastName: Yup.string().trim().required("Yetkili soyadı gerekli").max(60, "En fazla 60 karakter"),
    email: Yup.string().trim().required("Yetkili e-posta gerekli").email("Geçerli bir e-posta adresi giriniz"),
    phone: Yup.string().trim().required("Yetkili telefon gerekli").matches(PHONE_PATTERN, "Geçerli bir telefon numarası giriniz"),
    companyType: Yup.string().required("İşletme tipi seçiniz"),
    kvkkAccepted: Yup.boolean().oneOf([true], "Devam etmek için onay vermeniz gerekir"),
    companyTitle: Yup.string().trim().required("İşletme ünvanı gerekli").max(200, "En fazla 200 karakter"),
    nationalId: Yup.string().required("TC kimlik numarası gerekli").matches(NATIONAL_ID_PATTERN, "TC kimlik numarası 11 haneli olmalıdır"),
    taxOffice: Yup.string().trim().required("Vergi dairesi gerekli").max(100, "En fazla 100 karakter"),
    monthlyRevenue: Yup.string().required("Aylık ciro beklentisi gerekli"),
});

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");
const formatAmount = (digits) => (digits ? Number(digits).toLocaleString("tr-TR") : "");

/**
 * İşyerimPOS üye iş yeri başvuru formu — iki aşama: yetkili bilgileri, işletme bilgileri.
 * Alanlar firma kaydından önceden doldurulur; başvuru satış ekiplerine e-posta olarak gider.
 * Üstteki Geri ikinci aşamadan birinciye, birinci aşamadan bağlantı ekranına (onBack) döner.
 */
export default function IsyerimPosApplicationForm({ companyId, onBack }) {
    const [step, setStep] = useState(1);
    const [website, setWebsite] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [sentMessage, setSentMessage] = useState(null);

    const formik = useFormik({
        initialValues: {
            firstName: "", lastName: "", email: "", phone: "", companyType: "", kvkkAccepted: false,
            companyTitle: "", nationalId: "", taxOffice: "", monthlyRevenue: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            setSubmitting(true);
            setError(null);
            try {
                const res = await submitIsyerimPosApplication(companyId, {
                    firstName: values.firstName.trim(),
                    lastName: values.lastName.trim(),
                    email: values.email.trim(),
                    phone: values.phone.trim(),
                    businessType: COMPANY_TYPE_LABEL_MAP[values.companyType] || values.companyType,
                    kvkkAccepted: values.kvkkAccepted,
                    companyTitle: values.companyTitle.trim(),
                    nationalId: values.nationalId,
                    taxOffice: values.taxOffice.trim(),
                    monthlyRevenue: Number(values.monthlyRevenue),
                });
                if (res?.statusCode === 200) {
                    setSentMessage(res.message || "Başvurunuz iletildi.");
                } else {
                    setError(res?.message || "Başvurunuz iletilemedi. Lütfen daha sonra tekrar deneyin.");
                }
            } catch {
                setError("Başvurunuz iletilemedi. Lütfen daha sonra tekrar deneyin.");
            } finally {
                setSubmitting(false);
            }
        },
    });

    // Firma kaydındaki bilgilerle ön doldurma; kullanıcının yazdığı alan ezilmez.
    useEffect(() => {
        if (!companyId) return undefined;
        let cancelled = false;
        fetchIsyerimPosApplicationDefaults(companyId)
            .then((res) => {
                const d = res?.data;
                if (cancelled || !d) return;
                setWebsite(d.website || "");
                formik.setValues((current) => ({
                    ...current,
                    firstName: current.firstName || d.firstName || "",
                    lastName: current.lastName || d.lastName || "",
                    email: current.email || d.email || "",
                    phone: current.phone || d.phone || "",
                    companyType: current.companyType || d.companyType || "",
                    companyTitle: current.companyTitle || d.companyTitle || "",
                    nationalId: current.nationalId || d.nationalId || "",
                    taxOffice: current.taxOffice || d.taxOffice || "",
                }), false);
            })
            .catch(() => {});
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [companyId]);

    const goToStepTwo = async () => {
        const errors = await formik.validateForm();
        const stepErrors = STEP_ONE_FIELDS.filter((field) => errors[field]);
        if (stepErrors.length > 0) {
            formik.setTouched(Object.fromEntries(STEP_ONE_FIELDS.map((field) => [field, true])), false);
            return;
        }
        setStep(2);
    };

    const errorOf = (name) => (formik.touched[name] && formik.errors[name]) || null;

    const textField = (name, label, props = {}) => (
        <div className="mb-3">
            <label className="field-label d-block" htmlFor={`isyerimpos-app-${name}`}>{label}</label>
            <Input
                id={`isyerimpos-app-${name}`}
                name={name}
                value={formik.values[name]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={Boolean(errorOf(name))}
                {...props}
            />
            {errorOf(name) && <div className="invalid-feedback d-block">{errorOf(name)}</div>}
        </div>
    );

    const readOnlyField = (label, value) => (
        <div className="mb-3">
            <label className="field-label d-block">{label}</label>
            <Input value={value} readOnly disabled />
        </div>
    );

    if (sentMessage) {
        return (
            <div className="p-3 d-flex align-items-start gap-2" role="status"
                style={{ backgroundColor: "#e9f7ef", borderRadius: 14, borderLeft: "4px solid #2BA66B" }}>
                <i className="bx bx-check-circle fs-4" style={{ color: "#2BA66B" }} />
                <div>
                    <div className="fw-bold" style={{ color: "#1f2937" }}>Başvurunuz alındı</div>
                    <div className="small text-muted">{sentMessage}</div>
                    {onBack && (
                        <button type="button" className="btn btn-sm btn-light fw-semibold mt-2" style={{ borderRadius: 999 }} onClick={onBack}>
                            <i className="bx bx-left-arrow-alt" /> Bağlantı ekranına dön
                        </button>
                    )}
                </div>
            </div>
        );
    }

    const goBack = () => (step === 2 ? setStep(1) : onBack?.());

    return (
        <div>
            <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
                {(onBack || step === 2) && (
                    <button type="button" className="btn btn-sm btn-light fw-semibold d-inline-flex align-items-center gap-1 me-2"
                        style={{ borderRadius: 999, padding: ".4rem 1rem" }}
                        disabled={submitting} onClick={goBack}>
                        <i className="bx bx-left-arrow-alt" /> Geri
                    </button>
                )}
                {STEPS.map((s, index) => (
                    <React.Fragment key={s.no}>
                        {index > 0 && <div style={{ flex: "0 0 28px", height: 2, backgroundColor: step >= s.no ? NAVY : "#d9deea" }} />}
                        <div className="d-flex align-items-center gap-2" aria-current={step === s.no ? "step" : undefined}>
                            <span
                                className="d-inline-flex align-items-center justify-content-center fw-bold"
                                style={{
                                    width: 26, height: 26, borderRadius: "50%", fontSize: 13,
                                    color: step >= s.no ? "#fff" : "#8b93a7",
                                    backgroundColor: step >= s.no ? NAVY : "#e7eaf1",
                                }}
                            >
                                {s.no}
                            </span>
                            <span className="small fw-semibold" style={{ color: step === s.no ? "#1f2937" : "#8b93a7" }}>{s.label}</span>
                        </div>
                    </React.Fragment>
                ))}
            </div>

            {step === 1 && (
                <>
                    <div className="row">
                        <div className="col-md-6">{textField("firstName", "Yetkili Adı")}</div>
                        <div className="col-md-6">{textField("lastName", "Yetkili Soyadı")}</div>
                        <div className="col-md-6">{textField("email", "Yetkili E-posta", { type: "email" })}</div>
                        <div className="col-md-6">{textField("phone", "Yetkili Telefon", { type: "tel", placeholder: "05XX XXX XX XX" })}</div>
                        <div className="col-md-6">
                            <div className="mb-3">
                                <label className="field-label d-block" htmlFor="isyerimpos-app-companyType">İşletme Tipi</label>
                                <Input
                                    id="isyerimpos-app-companyType"
                                    type="select"
                                    name="companyType"
                                    value={formik.values.companyType}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    invalid={Boolean(errorOf("companyType"))}
                                >
                                    <option value="">Seçiniz</option>
                                    {COMPANY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </Input>
                                {errorOf("companyType") && <div className="invalid-feedback d-block">{errorOf("companyType")}</div>}
                            </div>
                        </div>
                        <div className="col-md-6">{readOnlyField("Başvurulacak Ürün", PRODUCT)}</div>
                        <div className="col-12">{readOnlyField("Web Sitesi", website)}</div>
                    </div>

                    <div className="form-check mb-1">
                        <Input
                            id="isyerimpos-app-kvkk"
                            type="checkbox"
                            name="kvkkAccepted"
                            checked={formik.values.kvkkAccepted}
                            onChange={formik.handleChange}
                            invalid={Boolean(errorOf("kvkkAccepted"))}
                        />
                        <label className="form-check-label small" htmlFor="isyerimpos-app-kvkk">
                            Kişisel verilerimin KVKK ve Aydınlatma Metni kapsamında işlenmesini kabul ediyorum.
                        </label>
                    </div>
                    {errorOf("kvkkAccepted") && <div className="invalid-feedback d-block mb-2">{errorOf("kvkkAccepted")}</div>}

                    <div className="d-flex justify-content-end mt-3">
                        <button type="button" className="btn btn-sm fw-semibold d-inline-flex align-items-center gap-1"
                            style={{ borderRadius: 999, padding: ".5rem 1.2rem", backgroundColor: NAVY, borderColor: NAVY, color: "#fff" }}
                            onClick={goToStepTwo}>
                            Devam Et <i className="bx bx-right-arrow-alt" />
                        </button>
                    </div>
                </>
            )}

            {step === 2 && (
                <>
                    <div className="row">
                        <div className="col-12">{textField("companyTitle", "İşletme Ünvanı")}</div>
                        <div className="col-md-6">
                            {textField("nationalId", "Yetkili TC Kimlik Numarası", {
                                inputMode: "numeric",
                                maxLength: 11,
                                onChange: (e) => formik.setFieldValue("nationalId", digitsOnly(e.target.value).slice(0, 11)),
                            })}
                        </div>
                        <div className="col-md-6">{textField("taxOffice", "Vergi Dairesi")}</div>
                        <div className="col-12">
                            <div className="mb-3">
                                <label className="field-label d-block" htmlFor="isyerimpos-app-monthlyRevenue">Aylık Ciro Beklentiniz</label>
                                <div className="input-group">
                                    <Input
                                        id="isyerimpos-app-monthlyRevenue"
                                        name="monthlyRevenue"
                                        inputMode="numeric"
                                        placeholder="Örn: 250.000"
                                        value={formatAmount(formik.values.monthlyRevenue)}
                                        onChange={(e) => formik.setFieldValue("monthlyRevenue", digitsOnly(e.target.value).slice(0, 13))}
                                        onBlur={formik.handleBlur}
                                        invalid={Boolean(errorOf("monthlyRevenue"))}
                                    />
                                    <span className="input-group-text fw-semibold" style={{ borderRadius: "0 14px 14px 0" }}>TL</span>
                                </div>
                                {errorOf("monthlyRevenue") && <div className="invalid-feedback d-block">{errorOf("monthlyRevenue")}</div>}
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="small mb-2" role="alert" style={{ color: "#c2255c" }}>
                            <i className="bx bx-error-circle me-1" />{error}
                        </div>
                    )}

                    <div className="d-flex justify-content-end mt-3">
                        <button type="button" className="btn btn-sm fw-semibold d-inline-flex align-items-center gap-1"
                            style={{ borderRadius: 999, padding: ".5rem 1.2rem", backgroundColor: NAVY, borderColor: NAVY, color: "#fff" }}
                            disabled={submitting} onClick={formik.handleSubmit}>
                            {submitting ? <Spinner size="sm" /> : <i className="bx bx-send" />} Başvuruyu Gönder
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

IsyerimPosApplicationForm.propTypes = {
    companyId: PropTypes.string,
    onBack: PropTypes.func,
};
